/*
 * cfg-cart-group.js
 * Pure kern voor het groepsslot op configurator-regels in de winkelwagen.
 * Geen DOM, geen fetch, geen window — alles hier is testbaar met node:test.
 *
 * Elke cart-regel uit de configurator draagt properties._config (de uid van die
 * configuratie) en properties._cfg_role:
 *   main   = de set zelf, draagt ook _cfg_lock_ids (het recept)
 *   locked = een verplicht zakje, zit vast aan de set
 *   addon  = afwerking/accessoire/express/extra zakjesset, blijft los verwijderbaar
 *
 * _config en _cfg_role komen allebei uit line-item-properties en zijn dus door
 * de klant te sturen via POST /cart/add.js. perUid en ROLLEN gebruiken daarom
 * allebei Object.create(null) — een gewoon {} object zou bij _config: '__proto__'
 * de groep overslaan (en Object.prototype vervuilen) en bij _config: 'toString'
 * crashen op een overgeërfde functiewaarde; met een gewone ROLLEN-literal zou
 * _cfg_role: 'constructor' (of 'toString', 'valueOf', 'hasOwnProperty', …) de
 * guard ongemerkt passeren en alsnog een lege spookgroep openen.
 * Een regel met een onbekende of ontbrekende rol opent nooit een nieuwe groep,
 * en van twee main-regels met dezelfde uid wint de eerste — zo hangt het gedrag
 * niet af van de volgorde van regels in de cart.
 */
(function (root, factory) {
  if (typeof module === "object" && module.exports) { module.exports = factory(); }
  else { root.CfgCartGroup = factory(); }
})(typeof self !== "undefined" ? self : this, function () {
  "use strict";

  var ROLLEN = Object.create(null);
  ROLLEN.main = true;
  ROLLEN.locked = true;
  ROLLEN.addon = true;

  function groepeerRegels(cart) {
    var items = (cart && cart.items) || [];
    var perUid = Object.create(null);
    var volgorde = [];

    items.forEach(function (item) {
      var props = item.properties || {};
      var uid = props._config;
      var rol = props._cfg_role;
      if (!uid || !rol || !ROLLEN[rol]) return;

      if (!perUid[uid]) {
        perUid[uid] = { uid: uid, main: null, locked: [], addon: [] };
        volgorde.push(uid);
      }
      if (rol === 'main') {
        if (!perUid[uid].main) perUid[uid].main = item;
      } else if (rol === 'locked') {
        perUid[uid].locked.push(item);
      } else if (rol === 'addon') {
        perUid[uid].addon.push(item);
      }
    });

    return volgorde.map(function (uid) { return perUid[uid]; });
  }

  /*
   * Let op: _cfg_lock_ids is bewust een lijst mét dubbelingen. Twee zakjessets in
   * dezelfde kleur worden door Shopify tot één regel met aantal 2 samengevoegd, dus
   * "hoeveel van deze variant hoort erbij" is een telling, geen ja/nee.
   *
   * Teruggave is drieledig, met opzet: `null` betekent "geen uitspraak te doen"
   * (geen main, geen recept, of een onleesbaar/onzinnig main.quantity) en moet met
   * rust gelaten worden — dit component schrijft ongevraagd in andermans winkelwagen,
   * dus bij twijfel niets doen. `{}` betekent "recept gelezen, maar het bevat na
   * filtering geen enkele geldige variant-id" (bijv. alleen rommel). Verwar de twee
   * niet: alleen `{}` mag een lege verwachting betekenen, `null` juist "niet weten".
   */
  function verwachteAantallen(groep) {
    var main = groep && groep.main;
    var recept = main && main.properties && main.properties._cfg_lock_ids;
    if (!recept) return null;

    var setAantal = parseInt(main.quantity, 10);
    if (isNaN(setAantal) || setAantal < 1) return null;

    var uit = {};
    String(recept).split(',').forEach(function (deel) {
      var id = parseInt(deel, 10);
      // id <= 0 wordt hier wél gefilterd, in werkelijkeAantallen niet: recept-id's
      // komen uit een klantstuurbare line-item-property (_cfg_lock_ids), terwijl
      // regel.id in werkelijkeAantallen rechtstreeks uit Shopify's eigen cart-JSON
      // komt. Dat vertrouwensverschil is bewust — niet gladstrijken voor symmetrie.
      if (isNaN(id) || id <= 0) return;
      uit[id] = (uit[id] || 0) + setAantal;
    });
    return uit;
  }

  function werkelijkeAantallen(groep) {
    var uit = {};
    ((groep && groep.locked) || []).forEach(function (regel) {
      var id = parseInt(regel.id, 10);
      if (isNaN(id)) return;
      var q = parseInt(regel.quantity, 10);
      uit[id] = (uit[id] || 0) + (isNaN(q) ? 0 : q);
    });
    return uit;
  }

  /*
   * De bewaker. Geeft altijd een object terug; lege toevoegingen én een lege
   * bijwerk-map betekent "niets doen". De browserlaag mag alleen in actie komen als
   * er daadwerkelijk iets in staat, anders gaat hij met zichzelf vechten.
   */
  function bepaalHerstel(cart) {
    var toevoegen = [];
    var bijwerken = {};

    groepeerRegels(cart).forEach(function (groep) {
      // Set weg, kinderen nog aanwezig: kinderen opruimen.
      if (!groep.main) {
        groep.locked.forEach(function (regel) { bijwerken[regel.key] = 0; });
        return;
      }

      // null = geen uitspraak (geen recept, of een onleesbaar aantal op de set).
      // Dan niets doen: dit is code die ongevraagd in andermans winkelwagen schrijft,
      // dus bij twijfel is stilstaan de veilige kant.
      var verwacht = verwachteAantallen(groep);
      if (!verwacht) return;
      var werkelijk = werkelijkeAantallen(groep);

      Object.keys(verwacht).forEach(function (id) {
        var doel = verwacht[id];
        var nu = werkelijk[id] || 0;
        if (nu === doel) return;

        if (nu < doel) {
          toevoegen.push({
            id: parseInt(id, 10),
            quantity: doel - nu,
            properties: { _config: groep.uid, _cfg_role: 'locked' }
          });
          return;
        }

        // Te veel: haal het overschot van de eerste regel met deze variant af.
        var teveel = nu - doel;
        groep.locked.forEach(function (regel) {
          if (teveel <= 0 || parseInt(regel.id, 10) !== parseInt(id, 10)) return;
          var nieuw = Math.max(0, regel.quantity - teveel);
          teveel -= (regel.quantity - nieuw);
          bijwerken[regel.key] = nieuw;
        });
      });
    });

    return { toevoegen: toevoegen, bijwerken: bijwerken };
  }

  /*
   * Vertaalt een actie op de setregel naar een update over de hele groep.
   * nieuwAantal 0 = verwijderen: dan gaan de addons mee (express voor een
   * verwijderde set is zinloos). Anders schalen alleen main en locked mee; de
   * addons blijven van de klant.
   */
  function bepaalGroepsUpdate(cart, uid, nieuwAantal) {
    var updates = {};
    var groep = null;
    groepeerRegels(cart).forEach(function (g) { if (g.uid === uid) groep = g; });
    if (!groep || !groep.main) return updates;

    var doel = parseInt(nieuwAantal, 10);
    if (isNaN(doel) || doel < 0) return updates;

    if (doel === 0) {
      updates[groep.main.key] = 0;
      groep.locked.forEach(function (r) { updates[r.key] = 0; });
      groep.addon.forEach(function (r) { updates[r.key] = 0; });
      return updates;
    }

    // Schaal op het recept, niet op de huidige regelaantallen: die kunnen door een
    // eerdere herstelronde afwijken, en dan is delen-en-afronden een gok. Met het
    // recept in de hand is het exact.
    var doelAantallen = verwachteAantallen({
      main: { quantity: doel, properties: groep.main.properties },
      locked: [], addon: []
    });
    updates[groep.main.key] = doel;
    if (!doelAantallen) return updates;   // geen recept: alleen de set zelf schalen

    // Per variant krijgt de eerste regel het volle aantal, eventuele extra regels 0.
    // Meerdere regels voor dezelfde variant horen niet te bestaan (Shopify voegt
    // gelijke regels samen), maar als ze er toch zijn moet de uitkomst kloppen.
    // Een locked-regel met een variant die niet in het recept staat, laten we met
    // rust — consistent met de "bij twijfel niets doen"-lijn van dit bestand.
    var nogTeGeven = {};
    Object.keys(doelAantallen).forEach(function (id) { nogTeGeven[id] = doelAantallen[id]; });
    groep.locked.forEach(function (r) {
      var id = parseInt(r.id, 10);
      var rest = nogTeGeven[id];
      updates[r.key] = (rest === undefined) ? r.quantity : rest;
      if (rest !== undefined) nogTeGeven[id] = 0;
    });
    return updates;
  }

  return {
    groepeerRegels: groepeerRegels,
    verwachteAantallen: verwachteAantallen,
    werkelijkeAantallen: werkelijkeAantallen,
    bepaalHerstel: bepaalHerstel,
    bepaalGroepsUpdate: bepaalGroepsUpdate
  };
});
